// frontend/src/admin/v2/hooks/useSettings.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

export function useSettings() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const inflightRef = useRef(null)

  const fetchAll = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/settings`, { headers: HEADERS, signal: c.signal })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      setError(e.message || 'Failed to load')
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [])

  /**
   * Flip one `source: "db"` setting. Writes to the database, not to an env var,
   * so the change takes effect without a deploy (N102 Stufe A, § 6.1).
   *
   * Throws on failure — the caller shows the error next to the row. There is no
   * abort here on purpose: this is a single user action, and cancelling it would
   * leave the row in an unknown state.
   */
  const saveSetting = useCallback(async (key, value) => {
    const r = await axios.put(
      `${API}/api/admin/settings/${encodeURIComponent(key)}`,
      { value },
      { headers: HEADERS },
    )
    const neu = r.data
    setData(prev => {
      if (!prev) return prev
      const grouped = {}
      for (const [cat, items] of Object.entries(prev.grouped || {})) {
        grouped[cat] = items.map(s => (s.key === key ? { ...s, ...neu, is_set: true, lesbar: true } : s))
      }
      return { ...prev, grouped }
    })
    return neu
  }, [])

  useEffect(() => {
    fetchAll()
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchAll])

  return { data, loading, error, refetch: fetchAll, saveSetting }
}
